import HomeQuickMenu from '@/components/HomeQuickMenu'
import { HomeSlider } from '@/components/HomeSlider'
import { PopularSearchWords } from '@/components/PopularSearchWords'
import { ProductCarouselPanel } from '@/components/ProductCarouselPanel/ProductCarouselPanel'
import ViewedProductList from '@/components/ViewedProductList'
import { BannerType } from '@/constants'
import { Meta } from '@/layouts/Meta'
import { Main } from '@/templates/Main'
import type { FlashSale, Menu, Post, Product, SystemConfig } from '@/types'
import {
  type BannerList,
  type Promotion,
  type PromotionProductCarousel,
} from '@/types'
import { getTranslate, setContext } from '@/utils/api-interceptors'
import {
  getBannerSSR,
  getBottomMenu,
  getConfigSSR,
  getExclusiveDeal,
  getFlashSale,
  getHeaderMenu,
  getHomepageProductCarousel,
  getKeyword,
  getPostsByType,
  getProductForYou,
  getQuickMenu,
  getSaleRegion,
  getViewedProducts,
} from '@/utils/endpoint'
import { getCookies } from 'cookies-next'
import { type GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

const HomePageBlogList = dynamic(
  () => import('@/components/HomePageBlogList'),
  {
    ssr: false,
  }
)
const HomeBanner = dynamic(() => import('@/components/HomeBanner'), {
  ssr: false,
})
const ProductCarousel = dynamic(() => import('@/components/ProductCarousel'), {
  ssr: false,
})
const Recommend = dynamic(() => import('@/components/Recommend'), {
  ssr: false,
})
type HomeProps = {
  exclusiveDeal: Promotion | null
  banner: BannerList[]
  productCarousel: PromotionProductCarousel[]
  quickMenu: Menu[][]
  tips: Post[]
  promotions: Post[]
  flashSale: FlashSale
  productForYou: Promotion
  viewedProducts: Product[]
  systemConfig?: SystemConfig
}
const Home = ({
  banner,
  productCarousel,
  exclusiveDeal,
  quickMenu,
  tips,
  promotions,
  flashSale,
  productForYou,
  viewedProducts,
  systemConfig,
}: HomeProps) => {
  const router = useRouter()
  const middleBanner = useMemo(() => {
    return banner.filter(banner => {
      return banner.type === BannerType.HOME && banner.status === 1
    })
  }, [banner])
  const mainSlider = useMemo(() => {
    return banner.filter(banner => {
      return banner.type === BannerType.MAIN && banner.status === 1
    })
  }, [banner])
  return (
    <Main
      meta={
        <Meta
          title={getTranslate(systemConfig?.seoMetaTitle)}
          description={getTranslate(systemConfig?.seoMetaDescription)}
          imageUrl={systemConfig?.seoImageDefault || ''}
          imageAlt={systemConfig?.websiteBrandName || ''}
          canonical={
            process.env.NEXT_PUBLIC_URL_WEBSITE + router.asPath ||
            process.env.NEXT_PUBLIC_URL_WEBSITE
          }
          noFollow={false}
          noIndex={false}
          mobileAlternate={{
            media: 'only screen and (max-width:991px))',
            href:
              process.env.NEXT_PUBLIC_URL_WEBSITE_MOBILE + router.asPath ||
              process.env.NEXT_PUBLIC_URL_WEBSITE_MOBILE ||
              '',
          }}
        />
      }
      page='homepage'
    >
      <HomeSlider sliders={mainSlider[0]?.items} />

      <div className='container relative z-[1] mx-auto mt-12 flex w-full flex-col gap-5 pb-5'>
        <HomeBanner
          banner={middleBanner[0]?.items}
          layout={middleBanner[0]?.layout}
          data={middleBanner[0]}
        />
        <HomeQuickMenu quickMenuData={quickMenu[0]} />
        <ViewedProductList data={viewedProducts} />
        <ProductCarouselPanel
          flashSale={flashSale}
          productCarousel={productCarousel}
        />
        <HomeBanner
          banner={middleBanner[1]?.items}
          layout={middleBanner[1]?.layout}
        />
        {exclusiveDeal && (
          <ProductCarousel
            headerClassName='min-h-[100px]'
            data={exclusiveDeal}
          />
        )}
        <HomeQuickMenu quickMenuData={quickMenu[1]} />
        <HomeBanner
          banner={middleBanner[2]?.items}
          layout={middleBanner[2]?.layout}
        />
        <Recommend data={productForYou} />
        <HomePageBlogList tips={tips} promotions={promotions} />
        <PopularSearchWords />
      </div>
    </Main>
  )
}

export default Home

export const getServerSideProps: GetServerSideProps = async ({
  req,
  res,
  locale,
}) => {
  const { token, language, messages } = await setContext(
    getCookies({ req, res }),
    {
      language: locale,
    }
  )

  const [
    bottomMenu,
    quickMenu,
    globalHeaderMenu,
    keywords,
    flashSale,
    productForYou,
    banner,
    systemConfig,
    productCarousel,
    exclusiveDeal,
    regions,
    tips,
    promotions,
    viewedProducts,
  ] = await Promise.all([
    getBottomMenu(),
    getQuickMenu(),
    getHeaderMenu(),
    getKeyword(),
    getFlashSale(),
    getProductForYou(),
    getBannerSSR(),
    getConfigSSR(),
    getHomepageProductCarousel(),
    getExclusiveDeal(),
    getSaleRegion(),
    getPostsByType('tips'),
    getPostsByType('promotion'),
    getViewedProducts(),
  ])
  return {
    props: {
      bottomMenu,
      quickMenu,
      globalHeaderMenu,
      keywords,
      productForYou,
      messages,
      flashSale,
      banner,
      language,
      systemConfig,
      productCarousel,
      exclusiveDeal,
      regions,
      token: token || null,
      tips,
      promotions,
      viewedProducts,
    },
  }
}
